import React, { useEffect, useState } from 'react'
import clsx from 'clsx'
import { FormikErrors, useFormik } from 'formik'
import * as Yup from 'yup'
import { UpdateFranchiseAccount } from '../../../graphql/mutation'
import { useMutation } from '@apollo/client'
import { toast } from 'react-toastify'

type Props = {
  nextStep: any
  prevStep: any
  formData: any
  setFormData: any
}
const OnboardingSchema = Yup.object().shape({
  organization: Yup.string()
    .matches(/^[a-zA-Z0-9(),.\- ]*$/, 'Organization contains invalid characters')
    .min(2, 'Minimum 2 symbols')
    .max(50, 'Maximum 50 symbols')
    .required('Organization is required'),
  Address: Yup.string()
    .matches(/^[a-zA-Z0-9(),.\- ]*$/, 'Address contains invalid characters')
    .min(2, 'Minimum 2 symbols')
    .max(50, 'Maximum 50 symbols')
    .required('Address is required'),
  DateOfEstablishment: Yup.date()
    .max(new Date(), 'Date of Incorporation cannot be in the future')
    .required('Date of Incorporation is required'),
  ContactNumber: Yup.string()
    .matches(/^\d{10}$/, 'Contact Number must be exactly 10 digits')
    .required('Contact Number is required')
})

const OrganizationDetails: React.FC<Props> = ({ nextStep, prevStep, formData, setFormData }) => {
  const [selectedOptions, setSelectedOptions] = useState({
    industryType: '',
    companyType: '',
    companySize: ''
  })

  const handleDropdownSelect = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const name = e.target.name
    const value = e.target.value

    setSelectedOptions((prevValue) => ({
      ...prevValue,
      [name]: value
    }))
  }

  const initialValues = {
    organization: !!formData ? formData.organization : '',
    Address: !!formData ? formData.registeredAddress : '',
    DateOfEstablishment: !!formData ? formData.dateOfIncorporation : '',
    ContactNumber: !!formData ? formData.businessContactNumber : ''
  }

  useEffect(() => {
    if (!!formData && !!formData.industry) {
      setSelectedOptions((prevValue) => ({
        ...prevValue,
        industryType: formData.industry
      }))
    }

    if (!!formData && !!formData.typeOfCompany) {
      setSelectedOptions((prevValue) => ({
        ...prevValue,
        companyType: formData.typeOfCompany
      }))
    }
    if (!!formData && !!formData.companySize) {
      setSelectedOptions((prevValue) => ({
        ...prevValue,
        companySize: formData.companySize
      }))
    }
  }, [formData])

  console.log(formData)

  const [updateFranchiseAccount] = useMutation(UpdateFranchiseAccount, {
    context: {
      headers: {
        'access-control-allow-methods': 'Franchise'
      }
    }
  })

  const formik = useFormik({
    initialValues,
    enableReinitialize: true,
    validationSchema: OnboardingSchema,
    onSubmit: (values) => {
      const updatedFormData = {
        ...formData,
        organization: values.organization,
        registeredAddress: values.Address,
        typeOfCompany: selectedOptions.companyType,
        industry: selectedOptions.industryType,
        companySize: selectedOptions.companySize,
        dateOfIncorporation: values.DateOfEstablishment,
        businessContactNumber: values.ContactNumber
      }

      setFormData(updatedFormData)
      updateFranchiseAccount({
        variables: {
          input: {
            id: formData.id,
            organization: values.organization,
            registeredAddress: values.Address,
            typeOfCompany: selectedOptions.companyType,
            industry: selectedOptions.industryType,
            companySize: selectedOptions.companySize,
            dateOfIncorporation: values.DateOfEstablishment,
            businessContactNumber: values.ContactNumber
          }
        },
        context: {
          headers: {
            'access-control-allow-methods': 'Franchise'
          }
        }
      })
        .then((res: any) => {
          if (res.data.updateFranchiseAccount.status === 200) {
            console.log(res)
            toast.success('Organization Details Updated Successfully')
            nextStep()
          } else {
            toast.error(res.data.updateFranchiseAccount.message)
          }
        })
        .catch((err) => {
          console.log(err)
        })
    }
  })

  const getErrorMessage = (error: string | string[] | FormikErrors<any> | FormikErrors<any>[]) => {
    if (typeof error === 'string') {
      return error
    } else if (Array.isArray(error)) {
      return error.join(', ')
    } else {
      return JSON.stringify(error)
    }
  }
  return (
    <>
      <div id='kt_account_settings_profile_details' className='collapse show'>
        <form id='kt_account_profile_details_form' className='form' onSubmit={formik.handleSubmit}>
          {formik.status && (
            <div className='mb-lg-15 alert alert-danger'>
              <div className='alert-text font-weight-bold'>{formik.status}</div>
            </div>
          )}

          <div className='card-body border-top p-9'>
            <div className='row mb-6'>
              <h1 className=''> Organization Details</h1>
              <p>Tell us little bit about your business.</p>
            </div>
            <div className='row mb-6'>
              <label className='col-lg-4 col-form-label required fw-semibold fs-6'>
                Organization
              </label>

              <div className='col-lg-8 fv-row'>
                <input
                  type='text'
                  {...formik.getFieldProps('organization')}
                  className={clsx(
                    'form-control form-control-lg form-control-solid',
                    {
                      'is-invalid': formik.touched.organization && formik.errors.organization
                    },
                    {
                      'is-valid': formik.touched.organization && !formik.errors.organization
                    }
                  )}
                  placeholder='Organization Name'
                />
                {formik.touched.organization && formik.errors.organization && (
                  <div className='fv-plugins-message-container'>
                    <div className='fv-help-block'>
                      <span role='alert'>{getErrorMessage(formik.errors.organization)}</span>
                    </div>
                  </div>
                )}
              </div>
            </div>

            <div className='row mb-6'>
              <label className='col-lg-4 col-form-label fw-semibold fs-6'>
                <span className='required'>Registered Address</span>
                <span className='ms-1' data-bs-toggle='tooltip' title='Registered Address'></span>
              </label>

              <div className='col-lg-8 fv-row'>
                <input
                  type='tel'
                  {...formik.getFieldProps('Address')}
                  className={clsx(
                    'form-control form-control-lg form-control-solid',
                    {
                      'is-invalid': formik.touched.Address && formik.errors.Address
                    },
                    {
                      'is-valid': formik.touched.Address && !formik.errors.Address
                    }
                  )}
                  placeholder='Registered Address'
                />
                {formik.touched.Address && formik.errors.Address && (
                  <div className='fv-plugins-message-container'>
                    <div className='fv-help-block'>
                      <span role='alert'>{getErrorMessage(formik.errors.Address)}</span>
                    </div>
                  </div>
                )}
              </div>
            </div>
            <div className='row mb-6'>
              <label className='col-lg-4 col-form-label required fw-semibold fs-6'>
                Contact Number
              </label>

              <div className='col-lg-8 fv-row'>
                <input
                  type='text'
                  {...formik.getFieldProps('ContactNumber')}
                  className={clsx(
                    'form-control form-control-lg form-control-solid',
                    {
                      'is-invalid': formik.touched.ContactNumber && formik.errors.ContactNumber
                    },
                    {
                      'is-valid': formik.touched.ContactNumber && !formik.errors.ContactNumber
                    }
                  )}
                  placeholder='Contact Number'
                />
                {formik.touched.ContactNumber && formik.errors.ContactNumber && (
                  <div className='fv-plugins-message-container'>
                    <div className='fv-help-block'>
                      <span role='alert'>{getErrorMessage(formik.errors.ContactNumber)}</span>
                    </div>
                  </div>
                )}
              </div>
            </div>

            <div className='row mb-6'>
              <label className='col-lg-4 col-form-label required fw-semibold fs-6'>
                What is your company type?
              </label>

              <div className='col-lg-8 fv-row'>
                <select
                  className=' form-control form-control-lg form-control-solid '
                  value={selectedOptions.companyType}
                  onChange={handleDropdownSelect}
                  name='companyType'
                >
                  <option key='blankKey' hidden>
                    Select
                  </option>
                  <option value='privateLimitedCompany'>Private Limited Company (Pvt. Ltd.)</option>
                  <option value='publicLimitedCompany'>Public Limited Company (Ltd.)</option>
                  <option value='limitedLiabilityPartnership'>
                    Limited Liability Partnership (LLP)
                  </option>
                  <option value='limitedLiabilityCompany'>Limited Liability Company(LLC)</option>
                  <option value='soleProprietorship'>Sole Proprietorship</option>
                  <option value='partnership'>Partnership</option>
                  <option value='onePersonCompany'> One Person Company (OPC)</option>
                </select>
              </div>
            </div>

            <div className='row mb-6'>
              <label className='col-lg-4 col-form-label required fw-semibold fs-6'>
                Company Size?
              </label>
              <div className='col-lg-8 fv-row'>
                <select
                  className=' form-control form-control-lg form-control-solid '
                  value={selectedOptions.companySize}
                  onChange={handleDropdownSelect}
                  name='companySize'
                >
                  <option key='blankKey' hidden>
                    Select
                  </option>
                  <option value='belowTwenty'>Below 20</option>
                  <option value='twentyOneToFifty'>21 to 50</option>
                  <option value='fiftyOneToTwoHundred'>51 to 100</option>
                  <option value='twoHundredOneToFiveHundred'>201 to 500</option>
                  <option value='fiveHundredOneToThousand'>501 to 1000</option>
                  <option value='thousandPlus'>1000+ </option>
                </select>
              </div>
            </div>
            <div className='row mb-6'>
              <label className='col-lg-4 col-form-label required fw-semibold fs-6'>
                What is your industry type?
              </label>

              <div className='col-lg-8 fv-row'>
                <select
                  className=' form-control form-control-lg form-control-solid '
                  value={selectedOptions.industryType}
                  onChange={handleDropdownSelect}
                  name='industryType'
                >
                  <option key='blankKey' hidden>
                    Select
                  </option>
                  <option value='Food & Beverages'>Food & Beverages</option>
                  <option value='Arts & Entertainment'>Arts & Entertainment</option>
                  <option value='Agency & Consulting'>Agency & Consulting</option>
                  <option value='Retail & E-commerce'>Retail & E-commerce</option>
                  <option value='Hospitality & Tourism'>Hospitality & Tourism</option>
                  <option value='Automative'> Automative</option>
                  <option value='Government & Nonprofit'>Government & Nonprofit</option>
                  <option value='Financial Services'>Financial Services</option>
                  <option value='Real Estate'>Real Estate</option>
                  <option value='Software & Technology'>Software & Technology</option>
                  <option value='Healthcare & Medical Services'>
                    Healthcare & Medical Services
                  </option>
                  <option value='Legal Services'>Legal Services</option>
                  <option value='Education'>Education</option>
                  <option value='Other'>Other</option>
                </select>
              </div>
            </div>
            <div className='row mb-6'>
              <label className='col-lg-4 col-form-label fw-semibold fs-6'>
                <span className='required'> Date of Incorporation:</span>
                <span className='ms-1' data-bs-toggle='tooltip' title='Registered Address'></span>
              </label>

              <div className='col-lg-8 fv-row'>
                <input
                  placeholder='Date of Establishment'
                  type='date'
                  autoComplete='off'
                  {...formik.getFieldProps('DateOfEstablishment')}
                  className={clsx(
                    'form-control form-control-lg form-control-solid',
                    {
                      'is-invalid':
                        formik.touched.DateOfEstablishment && formik.errors.DateOfEstablishment
                    },
                    {
                      'is-valid':
                        formik.touched.DateOfEstablishment && !formik.errors.DateOfEstablishment
                    }
                  )}
                />
                {formik.touched.DateOfEstablishment && formik.errors.DateOfEstablishment && (
                  <div className='fv-plugins-message-container'>
                    <div className='fv-help-block'>
                      <span role='alert'>{getErrorMessage(formik.errors.DateOfEstablishment)}</span>
                    </div>
                  </div>
                )}
              </div>
            </div>

            <div className='row mb-6 m-15'>
              <div className='col-lg-8 offset-lg-4'>
                <div className='d-flex justify-content-end'>
                  {/* "Next" button */}
                  <button
                    type='button'
                    className='btn btn-lg btn-secondary  mb-5 m-1'
                    onClick={prevStep}
                  >
                    Previous
                  </button>
                  <button
                    type='submit'
                    className='btn btn-lg btn-primary mb-5 m-1'
                    disabled={
                      formik.isSubmitting ||
                      !formik.isValid ||
                      !selectedOptions.companyType ||
                      !selectedOptions.companySize ||
                      !selectedOptions.industryType
                    }
                  >
                    Next
                  </button>
                </div>
              </div>
            </div>
          </div>
        </form>
      </div>
    </>
  )
}

export default OrganizationDetails
